import { getProvider } from './providerRegistry';
import { RequestQueue } from './requestQueue';
import type { CandleResponse, MarketDataRequest } from '../types/market';

const queue = new RequestQueue(3);
const cache = new Map<string, { expiresAt: number; value: CandleResponse }>();
const CACHE_TTL_MS = 10_000;

export async function requestMarketCandles(
  payload: MarketDataRequest,
  providerId = 'yahoo'
): Promise<CandleResponse> {
  const key = `${providerId}:${payload.symbol.toUpperCase()}:${payload.interval}:${payload.range}:${payload.extendedHours}`;
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) return { ...cached.value, fromCache: true, latencyMs: 0 };

  return queue.run(async () => {
    const secondLook = cache.get(key);
    if (secondLook && secondLook.expiresAt > Date.now()) {
      return { ...secondLook.value, fromCache: true, latencyMs: 0 };
    }

    const provider = getProvider(providerId);
    const startedAt = performance.now();
    const value = await withRetry(() => provider.getCandles(payload));
    const enriched: CandleResponse = {
      ...value,
      fromCache: false,
      latencyMs: Math.max(0, Math.round(performance.now() - startedAt))
    };
    cache.set(key, { expiresAt: Date.now() + CACHE_TTL_MS, value: enriched });
    return enriched;
  });
}

export function clearMarketDataCache(): void {
  cache.clear();
}

async function withRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === attempts) break;
      await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** (attempt - 1)));
    }
  }
  throw lastError;
}
